import { InternalServerError } from './errors';
import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/unstable/httpapi';
import { Schema } from 'effect';

/**
 * Expo Updates protocol v1 manifest endpoint — the self-hosted replacement for
 * EAS Update (docs/projects/active/release-rings.md, "Self-hosted OTA server").
 *
 * Unlike every other group here it has no derived client: the caller is the
 * `expo-updates` native module inside the RN binary, which speaks a fixed
 * protocol at a URL baked in at prebuild. It lives in the contract anyway so
 * the route is documented in OpenAPI alongside the rest of the API rather
 * than being an invisible raw router mount — which is why the endpoint
 * contradicts this package's usual "only add endpoints in use by clients"
 * rule (CLAUDE.md): the client is native, not generated.
 *
 * The handler returns a raw `HttpServerResponse`, so schema encoding is
 * bypassed (HttpApiBuilder returns the response untouched when the handler
 * yields one). That is load-bearing: the `expo-signature` header signs the
 * EXACT wire bytes, and a re-serialization between signing and sending would
 * break client verification. The success schema below is therefore
 * documentation of the wire format, never the thing that produces it.
 */
const manifestEndpoint = HttpApiEndpoint.get('GetExpoManifest', '/manifest', {
  // Binary, not the part schemas: the body is a multipart envelope, so calling
  // it bare JSON would be a lie even though a part inside it is JSON. The part
  // shapes are spelled out in the description below instead — a response schema
  // cannot be supplied from an endpoint `OpenApi.Transform`, which runs before
  // the deferred pass that patches compiled schemas in by document path.
  success: Schema.Uint8Array.pipe(
    HttpApiSchema.asUint8Array({ contentType: 'multipart/mixed' }),
    HttpApiSchema.status(200),
  ),
  error: InternalServerError,
})

  .annotate(OpenApi.Summary, 'Expo Updates manifest')
  .annotate(
    OpenApi.Description,
    [
      'Serves the OTA update manifest for a single (channel, platform, runtimeVersion)',
      'triple, per the Expo Updates protocol v1.',
      '',
      '**Request headers** (set by the `expo-updates` client, not by hand):',
      '`expo-protocol-version` (must be `1`), `expo-platform` (`ios` | `android`),',
      '`expo-channel-name` (`staging` | `production`), `expo-runtime-version`',
      '(an `@expo/fingerprint` hash), and optionally `expo-current-update-id`,',
      '`expo-expect-signature`, and `eas-client-id` (the sticky key for staged rollout).',
      '',
      '**Response** is always `multipart/mixed` with a single part:',
      'a `manifest` part carrying an `ExpoManifest` body when an update applies, or a',
      '`directive` part carrying an `ExpoDirective` body (`noUpdateAvailable`) when the',
      'device is already on the pointer head, no pointer exists, or a staged rollout put',
      'this device in the holdback bucket. When the client sends `expo-expect-signature`,',
      'the part carries an `expo-signature` header (RSA PKCS#1 v1.5 over the exact part bytes).',
      '',
      'Errors: `400` unsupported/invalid protocol headers, `406` the client cannot accept',
      '`multipart/mixed`, `500` signing is required but no signing key is configured.',
    ].join('\n'),
  )
  // Only `description` is set here. The sibling `content` is owned by the
  // deferred pass that patches compiled response schemas in by document path
  // after annotations run, so a hand-built schema written here is discarded —
  // which is why the part shapes live in the prose above rather than as a
  // `oneOf` under `content`.
  .annotate(
    OpenApi.Transform,
    (op: { responses?: Record<string, object> }) => ({
      ...op,
      responses: {
        ...op.responses,
        '200': {
          ...op.responses?.['200'],
          description:
            'multipart/mixed with a single part — `manifest` when an update applies, ' +
            '`directive` otherwise. Signed via the part-level `expo-signature` header ' +
            'when the client sends `expo-expect-signature`.',
        },
      },
    }),
  );

export const mobileUpdatesGroup = HttpApiGroup.make('MobileUpdates')
  .add(manifestEndpoint)
  .prefix('/mobile-updates');
